import { useCallback, useEffect, useMemo, useState } from 'react';
import { P2Button, P2ButtonGroup, P2TextDescription } from 'react-bootstrap-common-script';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { TextSubheader } from '../../../components/text/text-subheader';
import { Stepper, StepperItem } from '../../../components/stepper';
import { SectionCard } from '../../../components/card';
import { Separator } from '../../../components/separator';
import ProposedPaymentAllocationSection from '../../../components/financial-plan/proposed-payment-allocation';
import ProposedLoanRepaymentPlanSection from '../../../components/financial-plan/proposed-loan-repayment-plan-section';
import CurrentProposedOwnerFinancialPlanSection from '../../../components/financial-plan/current-owners-financial-plan-section';
import { DownloadSvg } from '../../../assets/svg';
import { useNavigate } from 'react-router-dom';
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum';
import { useAppSelector } from '../../../redux/hook';
import useStageDStepper from '../../../hooks/use-stage-d-stepper';
import { PolymerAxios } from '../../../utils/axios';
import { API_ApplicationStageUpdate, API_GetProposedFinancialPlan } from '../../../utils/api';
import { StageDProposedFinancialPlan } from '../../../models/classes/stage-d/Review-Application.class';
import { downloadFile } from '../../../utils/download-file';
import { useGetStageD1ObjQuery } from '../../../redux/services/getStageD';
import LottieLoader from '../../../components/loader/lottie-loader/lottie-loader';
import { ApplicationStatusEnum } from '../../../models/enum/application-status.enum';

const StageDProposedOwnerFinancialPlanPage = () => {
  const navigate = useNavigate();
  const [isPreliminary, setIsPreliminary] = useState<boolean>(false);
  const [infoboxStatus, setInfoboxStatus] = useState<'info' | 'success'>('info');
  const [infoboxTitle, setInfoboxTitle] = useState<string>('');
  const [infoboxText, setInfoboxText] = useState<string>('');
  const [hasLoan, setHasLoan] = useState<boolean>(true);
  const [proposedFinancialPlan, setProposedFinancialPlanData] = useState<StageDProposedFinancialPlan>();
  const [download_key, setDownloadKey] = useState<string>('');

  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const application_uuid = useAppSelector((state) => state.app.ApplicationIdSlice.applicationId);
  const { stepperItems } = useStageDStepper(loginUser.uinfin, application_uuid || '');
  const updatedStepperItems = stepperItems.map((item) =>
    item.id === 1 ? { ...item, title: 'Application is Reviewed' } : item.id === 2 ? { ...item, title: 'In-Principle Approval is Granted' } : item
  );
  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const activeStep = stepperItems.length;

  const applicationStep1FormData = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);
  const loginUserStructureStatus = useMemo(() => {
    const structure_status =
      applicationStep1FormData?.household.incoming_owners.find((e) => e.myinfo.uinfin === loginUser.uinfin)?.structure_status ??
      StructureStatusEnum.outgoing;
    return structure_status;
  }, [applicationStep1FormData?.household, loginUser.uinfin]);
  const { data, isLoading } = useGetStageD1ObjQuery(
    {
      application_uuid: application_uuid ?? '',
      uinfin: loginUser?.uinfin ?? '',
      stage: 'D4',
      structure: loginUserStructureStatus,
      singpass_token: singpassToken ?? '',
    },
    { skip: !application_uuid || !loginUserStructureStatus }
  );

  const fetchFinancialPlan = async () => {
    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).get(
        `${API_GetProposedFinancialPlan}?application_uuid=${application_uuid}`
      );
      const data: StageDProposedFinancialPlan = response.data;
      setProposedFinancialPlanData(data);
      setIsPreliminary(data.scheduled_completion_date === undefined);
      // Set hasLoan to true only if there is a non-zero, defined loan amount
      setHasLoan(data?.proposed_payment_allocation_payable_loan !== undefined && data.proposed_payment_allocation_payable_loan !== 0);
    } catch (error) {
      console.error('Error fetching financial plan:', error);
    }
  };

  useEffect(() => {
    if (proposedFinancialPlan === undefined) {
      fetchFinancialPlan();
    }
  }, []);

  useEffect(() => {
    if (isPreliminary) {
      setInfoboxStatus('info');
      setInfoboxTitle('Completion appointment date is still tentative');
      setInfoboxText(
        'The preliminary financial plan is based on your tentative completion appointment date. We will contact you to confirm the appointment date and time. If there is a change in your appointment date, a revised financial plan will be issued. '
      );
    } else {
      setInfoboxStatus('success');
      setInfoboxTitle('Completion appointment date is confirmed');
      setInfoboxText(
        'Your completion date is confirmed and the financial details are ready. Please review the financial plan before proceeding with completion.'
      );
    }
  }, [isPreliminary]);

  const handleNavigateNextE1 = useCallback(async () => {
    //API CALLING TO UPDATE STATUS
    let submittingData = {
      application_uuid: application_uuid,
      stage: 'E',
    };
    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(API_ApplicationStageUpdate, submittingData);
      if (response.status === 200) {
        navigate(`/application-coo?application_id=${application_uuid}`);
      }
    } catch (error) {
      console.log('Error update polymer status ', error);
    }
  }, [application_uuid, navigate, singpassToken]);

  const backNavigate = () => {
    // Check struct base on stepper items
    if (activeStep === 4) {
      // if PO is @ step 4, it is the retaining owner flow, so back should return to Current Owner FP
      navigate(`/application-coo/stage-d/review-financial-plan-current?application_id=${application_uuid}`);
      return;
    }
    // else return non retaining flow, back to IPA
    navigate(`/application-coo/stage-d/in-principle-approval?application_id=${application_uuid}`);
  };

  const handleDownload = useCallback(
    (key: string) => () => {
      console.log('downloading:', key);
      downloadFile(key, singpassToken);
    },
    [singpassToken]
  );

  const proposedFinancialPlanPdf = useMemo(() => {
    const proposedOwnersFinancialPlans = data?.folders?.filter((f) => f.name === 'Financial Plan for Proposed Owners') ?? [];
    if (proposedOwnersFinancialPlans.length === 0) return undefined;

    // 2) Sort them by submitted_date, newest first
    proposedOwnersFinancialPlans.sort((a, b) => {
      const dateA = new Date(a.comments[0]?.documents[0]?.submitted_date ?? '').getTime();
      const dateB = new Date(b.comments[0]?.documents[0]?.submitted_date ?? '').getTime();
      return dateB - dateA; // bigger (newer) dates bubble to index 0
    });

    return proposedOwnersFinancialPlans[0];
  }, [data]);

  useEffect(() => {
    if (proposedFinancialPlanPdf !== undefined) {
      try {
        if (proposedFinancialPlanPdf.comments.length > 0) {
          setDownloadKey(proposedFinancialPlanPdf.comments[0].documents[0].key);
        }
      } catch (e) {
        console.log('download-button process download_key', e);
      }
    } else {
      console.log('proposedFinancialPlanPdf is undefined');
    }
  }, [proposedFinancialPlanPdf]);

  const isNextButtonEnabled =
    !isPreliminary &&
    (startApplicationFormData?.polymer_status === ('PREPARE_FOR_COMPLETION' as ApplicationStatusEnum) ||
      startApplicationFormData?.polymer_status === ('PREPARE_FOR_COMPLETION_COMPLETE' as ApplicationStatusEnum));

  console.groupCollapsed('[DEBUG] Next button display conditions');
  console.log({
    polymerStatus: startApplicationFormData.polymer_status,
    isPreliminary: isPreliminary,
  });
  console.groupEnd();

  return (
    <ApplicationCooLayout>
      {isLoading && <LottieLoader />}
      {!isLoading && (
        <div className='view-application-stage-d-container'>
          <TextSubheader subheaderMain='Stage D: Process Application' subheaderSub={startApplicationFormData.selected_hdb_property?.address} />
          <div className='stage-d-stepper-wrapper'>
            <Stepper activeStep={activeStep} items={updatedStepperItems} style={{ margin: '62px auto' }} />
          </div>

          <div className='d-flex justify-content-center'>
            <SectionCard
              required={false}
              className='flex-fill'
              titleChildren={
                <>
                  <StepperItem variant='section_active' size={40} step={activeStep.toString()} marginBottom={'0px'} />
                  <P2TextDescription title={"Proposed Owners' Financial Plan"} />
                </>
              }
              contentChildren={
                <div className='polymer-stage-d-financial-plan-container'>
                  <div className='px-4'>
                    <CurrentProposedOwnerFinancialPlanSection
                      ownerType='proposed'
                      isPreliminary={isPreliminary}
                      infoboxStatus={infoboxStatus}
                      infoboxTitle={infoboxTitle}
                      infoboxText={infoboxText}
                      proposedFinancialPlanData={proposedFinancialPlan!}
                    />
                  </div>

                  <Separator variant='solid' style={{ margin: '48px 0 24px' }} />
                  <div className='px-4'>
                    <ProposedPaymentAllocationSection proposedFinancialPlanData={proposedFinancialPlan!} />
                  </div>

                  {hasLoan && (
                    <>
                      <Separator variant='solid' style={{ margin: '48px 0 24px' }} />
                      <div className='px-4'>
                        <ProposedLoanRepaymentPlanSection proposedFinancialPlanData={proposedFinancialPlan!} />
                      </div>
                    </>
                  )}
                </div>
              }
            />
          </div>

          <Separator style={{ margin: '32px 0' }} />

          <div className='polymer-application-coo__card-button'>
            <P2ButtonGroup>
              <P2Button variant={'outline-primary'} onClick={backNavigate}>
                Back
              </P2Button>
              {download_key !== '' && (
                <P2Button variant={'outline-primary'} onClick={handleDownload(download_key)} endIcon={<DownloadSvg strokeColor='#0C8188' />}>
                  Download financial plan
                </P2Button>
              )}
              {isNextButtonEnabled && (
                <P2Button onClick={handleNavigateNextE1} variant='primary-1'>
                  Next
                </P2Button>
              )}
            </P2ButtonGroup>
          </div>
        </div>
      )}
    </ApplicationCooLayout>
  );
};

export default StageDProposedOwnerFinancialPlanPage;
